// SPDX-License-Identifier: GPL-3.0-or-later
// Code-built pop bumper. The upstream bumper loads JSON meshes. This one is four solids of
// revolution. The origin is the centre of the bumper on the playfield.

import type { BufferGeometry } from "three";
import type { BumperSpec } from "../../shared/contracts.ts";
import { lathe } from "./shared.ts";

/** The four parts of a bumper. */
export interface BumperGeometry {
  readonly base: BufferGeometry;
  /** The socket under the ring. Upstream calls it the skirt. */
  readonly skirt: BufferGeometry;
  /** The ring. It moves down when the ball hits. */
  readonly ring: BufferGeometry;
  readonly cap: BufferGeometry;
  /** Height of the top of the cap above the playfield. */
  readonly height: number;
  /** Height of the top face of the cap. A decoration sits here. */
  readonly capTop: number;
}

/** Total height of a bumper as a part of heightScale. */
const HEIGHT_PER_SCALE = 0.6;

/** Build the geometry of a bumper. */
export function generateBumper(spec: BumperSpec): BumperGeometry {
  const r = spec.radius;
  const h = (spec.heightScale ?? 90) * HEIGHT_PER_SCALE;
  const baseTop = h * 0.12;
  const skirtTop = h * 0.5;
  const ringTop = h * 0.64;
  const capBottom = ringTop;
  const capTop = h;

  const base = lathe([
    [0, 0],
    [r, 0],
    [r, baseTop * 0.6],
    [r * 0.92, baseTop],
    [0, baseTop],
  ]);
  const skirt = lathe([
    [0, baseTop],
    [r * 0.85, baseTop],
    [r * 0.62, skirtTop],
    [0, skirtTop],
  ]);
  // The ring is hollow so that it can move down around the skirt.
  const ring = lathe([
    [r * 0.6, skirtTop],
    [r * 0.78, skirtTop],
    [r * 0.78, ringTop],
    [r * 0.6, ringTop],
  ]);
  const cap = lathe([
    [0, capBottom],
    [r * 0.7, capBottom],
    [r * 0.7, capBottom + (capTop - capBottom) * 0.35],
    [r * 0.52, capBottom + (capTop - capBottom) * 0.75],
    [r * 0.28, capTop - (capTop - capBottom) * 0.08],
    [0, capTop],
  ]);
  const names: Array<[BufferGeometry, string]> = [
    [base, "base"],
    [skirt, "skirt"],
    [ring, "ring"],
    [cap, "cap"],
  ];
  for (const [g, part] of names) g.name = `bumper.${part}-${spec.name}`;
  return { base, skirt, ring, cap, height: h, capTop };
}
